import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Image,
  Input,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Tag,
  Upload,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import PageTitle from "../../components/PageTitle";
import {
  createAdminTodo,
  deleteAdminTodo,
  getAdminTodos,
  updateAdminTodoStatus,
  uploadAdminTodoImage,
} from "../../api";

const { TextArea } = Input;

const AUDIT_OPTIONS = [
  { value: "pending", label: "未审核", color: "default" },
  { value: "approved", label: "审核通过", color: "success" },
  { value: "rejected", label: "已拒绝", color: "error" },
];

const PROCESS_OPTIONS = [
  { value: "pending", label: "未处理", color: "default" },
  { value: "done", label: "已处理", color: "processing" },
];

const TEST_OPTIONS = [
  { value: "pending", label: "待测试", color: "warning" },
  { value: "passed", label: "测试通过", color: "success" },
  { value: "failed", label: "未通过", color: "error" },
];

const labelOf = (options, value) =>
  options.find((o) => o.value === value)?.label || value;

const colorOf = (options, value) =>
  options.find((o) => o.value === value)?.color || "default";

const TodoList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [current, setCurrent] = useState(null);
  const [imageList, setImageList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const load = async (p = page) => {
    setLoading(true);
    try {
      const res = await getAdminTodos(p, pageSize);
      if (res?.success) {
        setData(res.data || []);
        setTotal(res.total || 0);
      } else {
        message.error(res?.error || "加载失败");
      }
    } catch {
      message.error("加载待办列表失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
  }, [page]);

  const openCreate = () => {
    form.resetFields();
    setImageList([]);
    setCreateOpen(true);
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    if (imageList.length >= 5) {
      message.warning("最多上传5张图片");
      onError?.(new Error("limit"));
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAdminTodoImage(file);
      if (!res?.success || !res?.url) throw new Error(res?.error || "上传失败");
      setImageList((prev) => [...prev, { uid: res.url, url: res.url, name: file.name }]);
      onSuccess?.(res);
    } catch (e) {
      message.error(e?.message || "图片上传失败");
      onError?.(e);
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = async () => {
    const values = await form.validateFields();
    try {
      const res = await createAdminTodo({
        content: values.content,
        images: imageList.map((f) => f.url),
      });
      if (!res?.success) throw new Error(res?.error);
      message.success("待办已创建");
      setCreateOpen(false);
      setPage(1);
      load(1);
    } catch (e) {
      message.error(e?.message || "创建失败");
    }
  };

  const openDetail = (row) => {
    setCurrent({ ...row });
    setDetailOpen(true);
  };

  const patchStatus = async (field, value) => {
    if (!current) return;
    const body = { [field]: value };
    try {
      const res = await updateAdminTodoStatus(current.id, body);
      if (!res?.success) throw new Error(res?.error);
      const updated = res.data;
      setCurrent(updated);
      setData((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      message.success("状态已更新");
    } catch (e) {
      message.error(e?.message || "更新失败");
    }
  };

  const handleDelete = (row) => {
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteAdminTodo(deleteTarget.id);
      if (!res?.success) throw new Error(res?.error || "删除失败");
      message.success("已删除");
      if (detailOpen && current?.id === deleteTarget.id) setDetailOpen(false);
      setDeleteTarget(null);
      load();
    } catch (e) {
      message.error(e?.message || "删除失败");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <PageTitle title="待办列表" />
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          onClick={openCreate}
          title="新增待办"
        />
      </div>

      {data.length === 0 && !loading ? (
        <Empty description="暂无待办" />
      ) : (
        <Row gutter={[16, 16]}>
          {data.map((item) => (
            <Col xs={24} sm={12} lg={8} key={item.id}>
              <Card
                hoverable
                loading={loading}
                onClick={() => openDetail(item)}
                style={{ height: "100%", cursor: "pointer" }}
                actions={[
                  <Button
                    key="delete"
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item);
                    }}
                  >
                    删除
                  </Button>,
                ]}
              >
                <div style={{ marginBottom: 8, minHeight: 44, color: "#1E293B", fontSize: 13, lineHeight: 1.5 }}>
                  {item.content}
                </div>
                {item.images?.length > 0 && (
                  <Image.PreviewGroup>
                    <Space wrap size={8}>
                      {item.images.slice(0, 3).map((url) => (
                        <Image
                          key={url}
                          src={url}
                          width={72}
                          height={72}
                          style={{ objectFit: "cover", borderRadius: 6 }}
                        />
                      ))}
                      {item.images.length > 3 && (
                        <span style={{ fontSize: 12, color: "#999" }}>+{item.images.length - 3}</span>
                      )}
                    </Space>
                  </Image.PreviewGroup>
                )}
                <div style={{ marginTop: 12 }}>
                  <Space wrap size={4}>
                    <Tag color={colorOf(AUDIT_OPTIONS, item.audit_status)}>
                      审核：{labelOf(AUDIT_OPTIONS, item.audit_status)}
                    </Tag>
                    {item.audit_status === "approved" && (
                      <Tag color={colorOf(PROCESS_OPTIONS, item.process_status)}>
                        处理：{labelOf(PROCESS_OPTIONS, item.process_status)}
                      </Tag>
                    )}
                    {item.audit_status === "approved" && item.process_status === "done" && (
                      <Tag color={colorOf(TEST_OPTIONS, item.test_status)}>
                        测试：{labelOf(TEST_OPTIONS, item.test_status)}
                      </Tag>
                    )}
                  </Space>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: "#94A3B8" }}>
                  {item.creator_name || "管理员"} · {item.create_time?.slice?.(0, 19)?.replace("T", " ")}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {total > pageSize && (
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            onChange={setPage}
            showSizeChanger={false}
          />
        </div>
      )}

      <Modal
        title="新增待办"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        okText="创建"
        confirmLoading={uploading}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="content"
            label="待办内容"
            rules={[{ required: true, message: "请输入待办内容" }]}
          >
            <TextArea rows={4} placeholder="请输入文字描述" maxLength={2000} showCount />
          </Form.Item>
          <Form.Item label={`图片（最多5张，已选 ${imageList.length}/5）`}>
            <Upload
              listType="picture-card"
              fileList={imageList}
              customRequest={handleUpload}
              onRemove={(file) => {
                setImageList((prev) => prev.filter((f) => f.uid !== file.uid));
              }}
              accept="image/*"
              disabled={imageList.length >= 5 || uploading}
            >
              {imageList.length < 5 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8, fontSize: 12 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="删除该待办？"
        open={!!deleteTarget}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onOk={confirmDelete}
        okText="删除"
        okButtonProps={{ danger: true }}
        confirmLoading={deleting}
        width={400}
      >
        删除后不可恢复，确定继续吗？
      </Modal>

      <Modal
        title="待办详情"
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={null}
        width={640}
      >
        {current && (
          <div>
            <div style={{ marginBottom: 16, fontSize: 14, lineHeight: 1.6, color: "#1E293B" }}>
              {current.content}
            </div>
            {current.images?.length > 0 && (
              <Image.PreviewGroup>
                <Space wrap size={8} style={{ marginBottom: 20 }}>
                  {current.images.map((url) => (
                    <Image
                      key={url}
                      src={url}
                      width={100}
                      height={100}
                      style={{ objectFit: "cover", borderRadius: 8 }}
                    />
                  ))}
                </Space>
              </Image.PreviewGroup>
            )}

            <div style={{ background: "#F8FAFC", padding: 16, borderRadius: 8 }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 12 }}>审核状态</div>
                <Select
                  style={{ width: "100%" }}
                  value={current.audit_status}
                  options={AUDIT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  onChange={(v) => patchStatus("audit_status", v)}
                />
              </div>

              {current.audit_status === "approved" && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 12 }}>处理状态</div>
                  <Select
                    style={{ width: "100%" }}
                    value={current.process_status}
                    options={PROCESS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    onChange={(v) => patchStatus("process_status", v)}
                  />
                </div>
              )}

              {current.audit_status === "approved" && current.process_status === "done" && (
                <div>
                  <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 12 }}>测试状态</div>
                  <Select
                    style={{ width: "100%" }}
                    value={current.test_status}
                    options={TEST_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    onChange={(v) => patchStatus("test_status", v)}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TodoList;
